import type {
  CcDeveloper,
  CcFlag,
  CcPackageStatus,
  CcPackageSummary,
} from "@/api/code-companion";

/** Lógica pura do board do Code Companion (testável sem render). */

export interface CcColumn {
  status: CcPackageStatus;
  label: string;
}

export const CC_COLUMNS: CcColumn[] = [
  { status: "backlog", label: "Backlog" },
  { status: "approved", label: "Approved" },
  { status: "in_progress", label: "In progress" },
  { status: "ready_for_deploy", label: "Ready for deploy" },
  { status: "done", label: "Done" },
  { status: "blocked", label: "Blocked" },
];

/**
 * As colunas que sao etapa do fluxo. `blocked` fica de fora: nao se avanca "ate"
 * bloqueado, se cai nele. Como sexta coluna de 270px fixos ela ficava fora da
 * viewport, e justo a coluna que pede atencao era a que ninguem via — a rolagem
 * horizontal existia e nao se anunciava.
 *
 * `CC_COLUMNS` continua inteira: o rotulo de status precisa achar `blocked`.
 */
export const CC_FLOW_COLUMNS: CcColumn[] = CC_COLUMNS.filter((c) => c.status !== "blocked");

export function groupByStatus(
  packages: CcPackageSummary[],
): Map<CcPackageStatus, CcPackageSummary[]> {
  const m = new Map<CcPackageStatus, CcPackageSummary[]>();
  for (const c of CC_COLUMNS) m.set(c.status, []);
  for (const p of packages) m.get(p.status)?.push(p);
  return m;
}

export function openFlagsByPackage(flags: CcFlag[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const f of flags) {
    if (f.status !== "open") continue;
    m.set(f.package_id, (m.get(f.package_id) ?? 0) + 1);
  }
  return m;
}

export interface BoardKpis {
  total: number;
  migrated: number;
  inProgress: number;
  blocked: number;
  pct: number;
}

export function computeBoardKpis(packages: CcPackageSummary[]): BoardKpis {
  const total = packages.length;
  const migrated = packages.filter(
    (p) => p.status === "done" || p.status === "ready_for_deploy",
  ).length;
  const inProgress = packages.filter((p) => p.status === "in_progress").length;
  const blocked = packages.filter((p) => p.status === "blocked").length;
  const pct = total ? Math.round((migrated / total) * 100) : 0;
  return { total, migrated, inProgress, blocked, pct };
}

/**
 * Resultado de soltar um card numa coluna: retorna o novo status se houve
 * mudança real (pacote existe e mudou de coluna), senão null (no-op).
 */
export function resolveDrop(
  packages: CcPackageSummary[],
  packageId: number,
  to: CcPackageStatus,
): CcPackageStatus | null {
  const pkg = packages.find((p) => p.id === packageId);
  if (!pkg || pkg.status === to) return null;
  return to;
}

export interface DevLoadRow {
  id: string;
  name: string;
  n: number;
  /** Fatia do trabalho atribuido, nao posicao contra o mais carregado. */
  pct: number;
  hasGrant: boolean;
}

export interface DevLoad {
  /** Vinculados com ao menos um pacote. */
  active: number;
  /** Vinculados ao projeto: quem tem permissao de trabalhar nele. */
  linked: number;
  rows: DevLoadRow[];
  /** Tem pacote e nao esta vinculado: toma 403 no proprio pacote. */
  unlinked: { id: string; name: string; n: number }[];
}

/**
 * Carga por desenvolvedor, ancorada no roster do projeto.
 *
 * A tela mostrava `2 of 1 linked`: o numerador contava `assigned_dev_id` distinto
 * nos pacotes e o denominador contava o roster. Sao conjuntos diferentes, entao a
 * fracao nao fechava — e o excedente nao era ruido de contagem, era gente com
 * pacote atribuido e sem `project_members`, que leva 403 ao abrir o proprio
 * trabalho. Por isso `unlinked` sai separado em vez de somado: e um problema de
 * acesso pra resolver, nao um dev a mais.
 */
export function computeDevLoad(
  packages: CcPackageSummary[],
  linkedDevs: CcDeveloper[],
): DevLoad {
  const porDev = new Map<string, number>();
  const nameByDevId = new Map<string, string>();
  for (const p of packages) {
    if (!p.assigned_dev_id) continue;
    porDev.set(p.assigned_dev_id, (porDev.get(p.assigned_dev_id) ?? 0) + 1);
    if (p.assigned_dev_name) nameByDevId.set(p.assigned_dev_id, p.assigned_dev_name);
  }

  const roster = new Set(linkedDevs.map((d) => d.user_id));
  // Denominador da barra: so o trabalho de quem esta no roster. Incluir o pacote
  // do nao-vinculado encolheria a fatia de todo mundo por causa de um erro de
  // acesso.
  const atribuido = linkedDevs.reduce((soma, d) => soma + (porDev.get(d.user_id) ?? 0), 0);

  const rows = linkedDevs.map((d) => {
    const n = porDev.get(d.user_id) ?? 0;
    return {
      id: d.user_id,
      name: d.name ?? d.email ?? d.user_id.slice(0, 8),
      n,
      pct: atribuido ? Math.round((n / atribuido) * 100) : 0,
      hasGrant: d.has_active_grant,
    };
  });

  const unlinked = Array.from(porDev.entries())
    .filter(([id]) => !roster.has(id))
    .map(([id, n]) => ({ id, name: nameByDevId.get(id) ?? id.slice(0, 8), n }));

  return {
    active: rows.filter((d) => d.n > 0).length,
    linked: linkedDevs.length,
    rows,
    unlinked,
  };
}

export interface BatchOutcome {
  ok: number;
  failed: number;
  /** true quando nao sobrou nada aprovado: a tela nao pode dizer que deu certo. */
  allFailed: boolean;
}

/**
 * Resultado real de uma aprovacao em lote.
 *
 * O botao do Gate B disparava N mutations soltas e mostrava um toast verde no
 * mesmo tick, antes de qualquer resposta. Com o backend recusando as quarenta, a
 * tela dizia "aprovando 40 pacotes" em verde e o board nao mexia — e o unico jeito
 * de descobrir era recarregar. Nenhuma das mutations tinha onError.
 */
export function summarizeBatch(results: PromiseSettledResult<unknown>[]): BatchOutcome {
  const ok = results.filter((r) => r.status === "fulfilled").length;
  const failed = results.length - ok;
  return { ok, failed, allFailed: results.length > 0 && ok === 0 };
}
